import { CircularProgress } from "@mui/material";
import * as XLSX from 'xlsx';
import { calculateDueDays, getMaintenanceStatus } from ".";

export const isValidDate = (dateString) => {
  const dateObj = new Date(dateString);
  const day = dateObj.getDate();
  const month = dateObj.getMonth() + 1;
  const year = dateObj.getFullYear();
  const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;

  const regex = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/\d{4}$/;
  return regex.test(formattedDate);
};

export const month = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const dataNotAvailableMsg = <div style={{ margin: '24px 0 20px 0', fontSize: 16 }}>Data not available</div>

export const generateReportMsg = <div className='w3-center' style={{ margin: '44px 0 20px 0', fontSize: 16 }}>Please click on generate report to view the report</div>

export const showCircularProgress = (
  <div className="w3-center" style={{ marginTop: 30 }}>
    <CircularProgress />
  </div>
)

export const isGreenUser = (username) => {
  if (!username) { return }
  const mailDomain = username.trim().split('@').pop();
  return ["greenvironmentindia.com", "greenvironment.in", "greenvironment.ai"].includes(mailDomain)

}

export const exportFullAssetTableToExcel = (fullResponseData, tabId, client_id) => {
  const exportData = [];

  const totalAssetCost = fullResponseData.reduce((grandTotal, fItem) => {
    return grandTotal + fItem.assets.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
  }, 0);

  fullResponseData.forEach((fItem, facilityIndex) => {
    const totalCost = fItem.assets.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
    const slNo = facilityIndex + 1;

    fItem.assets.forEach((item, index) => {
      const isFirst = index === 0;

      const maintenanceDueDays = calculateDueDays(
        item?.operation_start_date,
        item?.componentSchedule,
        item?.assetMaintenance,
        item?.operation_end_date
      );

      const maintenanceStatus = getMaintenanceStatus(
        maintenanceDueDays?.scheduleItem?.offset_time_days ?? null,
        item?.assetMaintenance,
        maintenanceDueDays?.maintenanceCompleted ?? null
      );

      const scheduleText = maintenanceDueDays?.overdueDays
        ? `Overdue By ${maintenanceDueDays?.overdueDays} Days`
        : maintenanceDueDays?.dueDays !== null
          ? `In ${maintenanceDueDays?.dueDays} Days`
          : '--';

      const scheduleDate = maintenanceDueDays?.actualMaintenanceDate
        ? `(${maintenanceDueDays?.actualMaintenanceDate})`
        : '';

      exportData.push({
        "Sl. No.": isFirst ? slNo : '',
        // Add Client column for leased-asset and sale when client_id is not present
        ...(!client_id && (tabId === 'leased-asset' || tabId === 'sale')
          ? { "Client": item.facility?.client?.name || '--' }
          : {}),
        "Facility": item.facility?.facility_name || '',
        "Location": item.facility?.location?.name || '',
        "Item Name": item?.assetName?.asset_name || '--',
        "Serial Number": item.serial_number || '--',
        "Associated Meter": item.assetMeter?.meter?.meterid || '--',
        "purchase Date": item.purchase_date ? new Date(item.purchase_date) : null,
        "Installation Date": item.installation_date ? new Date(item.installation_date) : null,
        "Asset Cost (₹)": item.cost ? parseFloat(item.cost) : null,
        "Particulars": item.particulars || '--',
        ...(tabId === 'distribution' || tabId === 'treatment'
          ? {
            "Component ID": item?.component_id || '--',
            "Rating": item?.rating || '--',
            "Unit Price (₹)": item.unit_price ? parseFloat(item.unit_price) : null,
            "Operation Start Date": item.operation_start_date ? new Date(item.operation_start_date) : null,
            "Operation End Date": item.operation_end_date ? new Date(item.operation_end_date) : null,
            "End Of Life Date": item.end_of_life_date ? new Date(item.end_of_life_date) : null,
            "Maintenance Schedule": `${scheduleText} ${scheduleDate}`.trim(),
            "Maintenance Status": maintenanceStatus?.status || '--',
          }
          : {}),
        ...(tabId === 'leased-asset'
          ? {
            "Bill Number": item?.bill_number || '--',
            "Vendor Name": item?.vendor_name || '--',
            "Warranty End Date": item.warranty_end_date ? new Date(item.warranty_end_date) : null,
          }
          : {}),
        ...(isFirst ? { "Total Facility Cost(₹)": totalCost } : { "Total Facility Cost(₹)": '' }),
      });
    });
  });

  if (exportData?.length > 0) {
    const finalRow = Object?.fromEntries(
      Object?.keys(exportData[0])?.map((key) => [key, ''])
    );
    finalRow["Total Assets Value"] = `Grand Total: ₹ ${totalAssetCost.toFixed(1)}`;
    exportData.push(finalRow);
  }

  const normalizedCategory = tabId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `${normalizedCategory}`);
  XLSX.writeFile(workbook, client_id ? `client_assets_${client_id}_export.xlsx` : `assets_export.xlsx`);
}

export const exportBusinessDashboardToExcel = (dashboardData) => {
  const exportData = [];
  const merges = [];

  // Column indices for merged columns (0-indexed)
  const COL_TOTAL_CONTRACT_VALUE = 16;
  const COL_TOTAL_INVOICED = 19;
  const COL_TOTAL_OUTSTANDING = 24;

  // Group by facility to calculate totals and merge cells
  const facilityGroups = {};
  dashboardData.forEach(row => {
    const key = `${row.client_group}||${row.facility_name}`;
    if (!facilityGroups[key]) {
      facilityGroups[key] = {
        client_type: row.client_type,
        client_group: row.client_group,
        company_name: row.company_name,
        facility_name: row.facility_name,
        segment: row.segment,
        customer_status: row.customer_status,
        orders: []
      };
    }
    facilityGroups[key].orders.push(row);
  });

  const facilityGroupsArray = Object.values(facilityGroups);
  let currentRow = 1; // Row 0 is the header

  facilityGroupsArray.forEach((facility) => {
    const orders = facility.orders;
    const startRow = currentRow;

    // Calculate totals for this facility
    const totalContractValue = orders.reduce((sum, o) => sum + (parseFloat(o.contract_value) || 0), 0);
    const totalInvoiced = orders.reduce((sum, o) => sum + (parseFloat(o.total_invoiced_amount) || 0), 0);
    const totalOutstanding = orders.reduce((sum, o) => sum + (parseFloat(o.outstanding) || 0), 0);

    orders.forEach((row, index) => {
      const isFirst = index === 0;

      exportData.push({
        "Client Type": facility.client_type || '--',
        "Client Group": facility.client_group || '--',
        "Company Name": facility.company_name || '--',
        "Facility": row.facility_name || '--',
        "Segment": facility.segment || '--',
        "BD Owner": row.bd_owner?.name || '--',
        "Project Owner": row.project_owner?.name || '--',
        "SO/PO No": row.so_po_no || '--',
        "Customer Status": facility.customer_status || '--',
        "Status": row.status || '--',
        "Contract Type": row.contract_type || '--',
        "Billing Cycle": row.billing_cycle || '--',
        "PO Date": row.po_date ? new Date(row.po_date) : null,
        "PO Start Date": row.po_start_date ? new Date(row.po_start_date) : null,
        "PO Expiry Date": row.po_expiry_date ? new Date(row.po_expiry_date) : null,
        "Contract Value (₹)": row.contract_value ? parseFloat(row.contract_value) : null,
        "Total Contract Value (₹)": isFirst ? totalContractValue : null,
        "Invoiced Date": row.last_invoiced_date ? new Date(row.last_invoiced_date) : null,
        "Invoiced (₹)": row.total_invoiced_amount ? parseFloat(row.total_invoiced_amount) : null,
        "Total Invoiced (₹)": isFirst ? totalInvoiced : null,
        "PO to Invoice Date": row.po_to_invoice_days !== null ? row.po_to_invoice_days : null,
        "Outstanding (₹)": row.outstanding ? parseFloat(row.outstanding) : null,
        "Invoice Numbers": row.outstanding_invoice_numbers || '--',
        "Aging (Days)": row.aging_days || 0,
        "Total Outstanding (₹)": isFirst ? totalOutstanding : null,
        "Payment Realization Date": row.payment_realization_date ? new Date(row.payment_realization_date) : null,
        "Next Invoice Date": row.next_invoice_date ? new Date(row.next_invoice_date) : null,
      });
      currentRow++;
    });

    // Add merge ranges for facilities with more than 1 order
    const endRow = currentRow - 1;
    if (orders.length > 1) {
      // Merge: Total Contract Value, Total Invoiced, Total Outstanding
      [COL_TOTAL_CONTRACT_VALUE, COL_TOTAL_INVOICED, COL_TOTAL_OUTSTANDING].forEach(col => {
        merges.push({ s: { r: startRow, c: col }, e: { r: endRow, c: col } });
      });
    }
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  worksheet['!merges'] = merges;
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Business Dashboard');
  XLSX.writeFile(workbook, `business_dashboard_export.xlsx`);
}

export const exportInvoiceScheduleToExcel = (months, rows, subTabLabel, fyYear) => {
  const exportData = [];

  const fmtIso = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  rows.forEach((row) => {
    const base = {
      'Client Group': row.client_group || '',
      'Company / Facility': row.company_name || '',
      'Facility': row.facility || '',
      'SO / PO No': row.so_no || '',
      'Cycle': row.cycle || '',
      'Basis': row.basis || 'Prepaid',
      'Status': row.status || '',
      'SO Start': fmtIso(row.so_start),
      'SO End': fmtIso(row.so_end),
      'Renewal Date': fmtIso(row.renewal_date),
    };

    months.forEach((mo, mi) => {
      const slot = row.months[mi] || {};
      base[`${mo} Planned ₹`]   = slot.planned   || '';
      base[`${mo} Actual ₹`]    = slot.actual    || '';
      base[`${mo} Invoice#`]     = (slot.invoiceNumbers || []).join(', ');
      base[`${mo} Collected ₹`] = slot.collected || '';
    });

    base['Total Planned ₹']   = row.totalPlanned   || 0;
    base['Total Actual ₹']    = row.totalActual    || 0;
    base['Total Collected ₹'] = row.totalCollected || 0;

    exportData.push(base);
  });

  const safeLabel = subTabLabel.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').slice(0, 28);
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, safeLabel);
  XLSX.writeFile(workbook, `invoice_schedule_${safeLabel}_FY${fyYear}-${String(fyYear + 1).slice(2)}.xlsx`);
}

export const exportRenewalToExcel = (rows, latestComments, subTabLabel, fyYear) => {
  const fmtIso = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const parseComment = (c) => {
    if (!c) return { author: '', body: '' };
    const match = c.description?.match(/^\[(.+?)\]:\s*/);
    if (match) return { author: match[1], body: c.description.slice(match[0].length) };
    return { author: c.commented_by || '', body: c.description || '' };
  };

  const statusLabel = { not_renewed: 'Not Renewed', pending: 'Pending (Active)', renewed: 'Renewed' };
  const urgencyLabel = {
    red:            '<30 Days',
    amber:          '30-90 Days',
    blue:           '>90 Days',
    expired_recent: 'Recently Expired (0-90d)',
    expired_old:    'Expired (>90d ago)',
  };

  const exportData = rows.map((r) => {
    const latest = (latestComments && latestComments[r.salesorder_id]) || r.latest_comment;
    const { author, body } = parseComment(latest);
    return {
      'Client Group':       r.client_group   || '',
      'Company':            r.company_name   || '',
      'Facility':           r.facility_name  || '',
      'SO / PO No':         r.so_number      || '',
      'Contract Type':      r.contract_type  || '',
      'Billing Cycle':      r.billing_cycle  || '',
      'Renewal Due':        fmtIso(r.po_expiry_date),
      'Days':               r.days_until_expiry,
      'Urgency':            urgencyLabel[r.urgency_bucket] || '',
      'Status':             statusLabel[r.tracker_status]  || '',
      'Contract Value (₹)': r.contract_value || 0,
      'Owner':              r.renewal_owner?.name || '',
      'Latest Comment':     body,
      'Comment By':         author,
      'Comment Date':       latest?.date || '',
    };
  });

  const safeLabel = subTabLabel.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').slice(0, 28);
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook  = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, safeLabel);
  XLSX.writeFile(workbook, `renewal_${safeLabel}_FY${fyYear}-${String(fyYear + 1).slice(2)}.xlsx`);
}